import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import type { Stage } from '../../types';
import * as api from '../../api';

export interface ConvertToOpportunityModalProps {
  leadId: string;
  leadName?: string;
  onConvert: (request: api.ConvertLeadRequest) => Promise<api.ConvertLeadResponse | null>;
  onClose: () => void;
  onSuccess?: (opportunityId: string) => void;
}

export function ConvertToOpportunityModal({
  leadName,
  onConvert,
  onClose,
  onSuccess,
}: ConvertToOpportunityModalProps): ReactNode {
  const [opportunityName, setOpportunityName] = useState(
    leadName ? `${leadName} - Opportunity` : ''
  );
  const [amount, setAmount] = useState('');
  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [stageId, setStageId] = useState('');
  const [stages, setStages] = useState<Stage[]>([]);
  const [loadingStages, setLoadingStages] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load stages
  useEffect(() => {
    async function loadStages() {
      try {
        const stagesData = await api.fetchStages();
        const sortedStages = [...stagesData].sort((a, b) => a.order - b.order);
        setStages(sortedStages);
        
        // Select first non-terminal stage as default
        const defaultStage = sortedStages.find(s => !s.is_terminal);
        if (defaultStage) {
          setStageId(defaultStage.id);
        } else if (sortedStages.length > 0) {
          setStageId(sortedStages[0].id);
        }
      } catch (err) {
        setError('Failed to load pipeline stages');
      } finally {
        setLoadingStages(false);
      }
    }
    loadStages();
  }, []);

  // Set default expected close date to 30 days from now
  useEffect(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    setExpectedCloseDate(date.toISOString().split('T')[0]);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!opportunityName.trim()) {
      setError('Opportunity name is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    const result = await onConvert({
      opportunity_name: opportunityName.trim(),
      stage_id: stageId || undefined,
      amount: amount ? parseInt(amount, 10) : undefined,
      currency: 'USD',
      expected_close_date: expectedCloseDate || undefined,
    });

    setSubmitting(false);

    if (result) {
      onSuccess?.(result.opportunity.id);
      onClose();
    } else {
      setError('Failed to convert lead');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Convert to Opportunity
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Opportunity Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Opportunity Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={opportunityName}
                onChange={(e) => setOpportunityName(e.target.value)}
                placeholder="Enter opportunity name"
                className="w-full"
                required
                autoFocus
              />
            </div>

            {/* Value */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value (USD)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  $
                </span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  min="0"
                  className="w-full pl-8"
                />
              </div>
            </div>

            {/* Expected Close Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expected Close Date
              </label>
              <input
                type="date"
                value={expectedCloseDate}
                onChange={(e) => setExpectedCloseDate(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Stage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Initial Stage
              </label>
              {loadingStages ? (
                <div className="h-10 bg-gray-100 animate-pulse rounded-md"></div>
              ) : stages.length === 0 ? (
                <p className="text-sm text-yellow-600">
                  No pipeline stages configured. A default stage will be used.
                </p>
              ) : (
                <select
                  value={stageId}
                  onChange={(e) => setStageId(e.target.value)}
                  className="w-full"
                >
                  {stages.map(stage => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                      {stage.is_terminal && ` (${stage.terminal_type})`}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !opportunityName.trim()}
            >
              {submitting ? 'Converting...' : 'Convert to Opportunity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ConvertToOpportunityModal;
